import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell, PanImage } from "../components/PageShell";
import heroImg from "../assets/planet-pan-hero.webp";

const GALLERY = [
  { id: "IiX-PiMQ.jpg", alt: "Planet Pan teens at the steel pans on the wharf" },
  { id: "ywXG3nkg.jpg", alt: "Close-up of young pannists performing under the tent" },
  { id: "KoaK9ekw.jpg", alt: "Planet Pan in purple tees behind the steel drums" },
  { id: "W8tAi1-A.jpg", alt: "Player with hula hoop on the wharf during a break" },
  { id: "zG_WbrOw.jpg", alt: "Three Planet Pan players sharing a laugh" },
  { id: "sTTdxAUw-55fa6b4.jpg", alt: "Planet Pan members on a boat heading to a gig" },
];

export const Route = createFileRoute("/planet-pan")({
  head: () => ({
    meta: [
      { title: "Planet Pan Steel Band — The Pan Institute" },
      {
        name: "description",
        content:
          "Planet Pan — a high-energy after-school steel band for teens on the Blue Hill Peninsula of Maine. Founded 1993 by Nigel Chase.",
      },
      { property: "og:title", content: "Planet Pan Steel Band" },
      {
        property: "og:description",
        content:
          "An after-school steel band for teens on the Blue Hill Peninsula. Three decades of young pannists.",
      },
      { property: "og:image", content: heroImg },
      { name: "twitter:image", content: heroImg },
    ],
  }),
  component: PlanetPanPage,
});

const TIMELINE = [
  {
    year: "1993",
    title: "Founded",
    body: "Nigel Chase starts Planet Pan as an after-school steel band for teens on the Blue Hill Peninsula.",
  },
  {
    year: "Next 10 years",
    title: "Carl takes the helm",
    body: "Carl Chase teaches the band for a decade, building a generation of young pannists on the peninsula.",
  },
  {
    year: "2004",
    title: "Nigel returns",
    body: "Nigel comes back to direct the band — touring exciting venues and teaching workshops across the state and beyond.",
  },
  {
    year: "Today",
    title: "On hiatus",
    body: "Planet Pan is currently not performing or running as a class. Recordings remain — and the door is left open for the next chapter.",
  },
];

const FACTS = [
  { k: "1993", v: "Founded" },
  { k: "30+", v: "Years of teens" },
  { k: "Maine", v: "Blue Hill Peninsula" },
];

function PlanetPanPage() {
  return (
    <PageShell
      panBackdrop={true}
      tag=""
      title={<>Planet <span className="text-primary">Pan</span>.</>}
      intro="A high-energy after-school steel band for teens on the Blue Hill Peninsula of Maine — founded 1993 by Nigel Chase."
    >
      {/* Stat strip */}
      <dl className="grid grid-cols-3 divide-x divide-border/40 rounded-2xl border border-border/40 bg-card/30">
        {FACTS.map((s) => (
          <div key={s.v} className="px-2 sm:px-4 py-6 text-center min-w-0">
            <dt className="font-display italic text-2xl sm:text-3xl md:text-4xl text-primary leading-none break-words">
              {s.k}
            </dt>
            <dd className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.35em] text-foreground/55 break-words">
              {s.v}
            </dd>
          </div>
        ))}
      </dl>

      {/* Editorial intro — two columns */}
      <section className="mt-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80 mb-4">
            About the band
          </div>
          <h2 className="font-display italic text-3xl md:text-4xl leading-[1.05]">
            Three decades<br />of young pannists.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-5 text-foreground/75 leading-relaxed">
          <p className="first-letter:font-display first-letter:italic first-letter:text-6xl first-letter:text-primary first-letter:float-left first-letter:mr-2 first-letter:leading-[0.85] first-letter:mt-1">
            Planet Pan was the after-school spark for teens on the Blue Hill
            Peninsula — high energy, big sound, and the kind of room where a
            beginner could turn into a real player by the end of a season.
          </p>
          <p>
            Founded in 1993 by Nigel Chase and taught by Carl Chase for ten
            years, the band travelled to festivals and workshops across Maine
            and beyond once Nigel returned to direct in 2004.
          </p>
          <p>
            The class is on pause for now, but the recordings — and the
            players who came through it — are still very much around.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="mt-24">
        <div className="flex items-end justify-between gap-6 mb-10 border-b border-border/40 pb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
              The story
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">
              A working chronology
            </h2>
          </div>
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.35em] text-foreground/40">
            1993 — today
          </span>
        </div>
        <ol className="divide-y divide-border/40 border-y border-border/40">
          {TIMELINE.map((t, i) => (
            <li
              key={i}
              className="grid md:grid-cols-12 gap-4 py-7 md:py-9 px-1 items-baseline group hover:bg-foreground/[0.015] transition-colors"
            >
              <div className="md:col-span-3 font-display italic text-xl md:text-2xl text-primary">
                {t.year}
              </div>
              <div className="md:col-span-9">
                <h3 className="font-display italic text-xl md:text-2xl">
                  {t.title}
                </h3>
                <p className="mt-2 text-[14px] text-foreground/65 leading-relaxed max-w-2xl">
                  {t.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Gallery — editorial mosaic */}
      <section className="mt-24">
        <div className="flex items-end justify-between gap-6 mb-10 border-b border-border/40 pb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
              In the field
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">
              Planet Pan, on the wharf
            </h2>
          </div>
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.35em] text-foreground/40">
            Photographs
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {GALLERY.map((g) => (
            <figure
              key={g.id}
              className="relative overflow-hidden rounded-2xl border border-border/40 bg-card/30 group aspect-[4/3]"
            >
              <PanImage
                id={g.id}
                alt={g.alt}
                className="transition-transform duration-700 group-hover:scale-105"
                width={1200}
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                aria-hidden
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 text-[11px] tracking-wide text-foreground/85 opacity-0 group-hover:opacity-100 transition-opacity">
                {g.alt}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-24 rounded-3xl border border-border/40 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
              Hear the band
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              The recordings are still out there.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Link
              to="/recordings"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/10 whitespace-nowrap"
            >
              Listen
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 border border-primary/50 text-primary text-[11px] tracking-[0.3em] uppercase rounded-full hover:bg-primary/10 transition whitespace-nowrap"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
